import { DotDotDot, EyeIcon } from "../../icons/dashboard";
import { Link } from "react-router-dom";
import { Tab } from "../../components/tab";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchDataupBalance,
  fetchSeagmBalance,
  fetchTotalUsers,
  fetchTotalTransactions,
  selectDashboardDataupBalance,
  selectDashboardDataupBalanceError,
  selectDashboardDataupBalanceLoading,
  selectDashboardSeagmBalance,
  selectDashboardSeagmBalanceError,
  selectDashboardSeagmBalanceLoading,
  selectDashboardTotalUsers,
  selectDashboardTotalUsersError,
  selectDashboardTotalUsersLoading,
  selectDashboardTotalTransactions,
  selectDashboardTotalTransactionsError,
  selectDashboardTotalTransactionsLoading,
} from "../../features/auth/store/dashboardStatSlice";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
} from "recharts";
import { BarChart, Bar, ResponsiveContainer } from "recharts";
import { SummaryProps, LeadershipProps } from "./types";
import { formatCurrency } from "../../utils/utils";

const formatNumber = (value: number) => {
  return value.toLocaleString();
};

const CardSpinner = () => (
  <div className="flex items-center py-1">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
  </div>
);

export const Summary = (prop: SummaryProps) => {
  const dispatch = useAppDispatch();
  const dataupBalance = useAppSelector(selectDashboardDataupBalance);
  const dataupBalanceLoading = useAppSelector(
    selectDashboardDataupBalanceLoading
  );
  const dataupBalanceError = useAppSelector(selectDashboardDataupBalanceError);
  const seagmBalance = useAppSelector(selectDashboardSeagmBalance);
  const seagmBalanceLoading = useAppSelector(
    selectDashboardSeagmBalanceLoading
  );
  const seagmBalanceError = useAppSelector(selectDashboardSeagmBalanceError);
  const totalUsers = useAppSelector(selectDashboardTotalUsers);
  const totalUsersLoading = useAppSelector(selectDashboardTotalUsersLoading);
  const totalUsersError = useAppSelector(selectDashboardTotalUsersError);
  const totalTransactions = useAppSelector(selectDashboardTotalTransactions);
  const totalTransactionsLoading = useAppSelector(
    selectDashboardTotalTransactionsLoading
  );
  const totalTransactionsError = useAppSelector(
    selectDashboardTotalTransactionsError
  );

  useEffect(() => {
    dispatch(fetchDataupBalance());
    dispatch(fetchSeagmBalance());
    dispatch(fetchTotalUsers());
    dispatch(fetchTotalTransactions());
  }, [dispatch]);

  return (
    <div className="row-4">
      <div
        className={
          seagmBalance && seagmBalance > 2000
            ? `bg-pry-25 min-w-[234px] rounded-xl`
            : `bg-warn-25 min-w-[234px] rounded-xl`
        }
      >
        <div className="col-6 py-4 px-3">
          <div className="row-2 justify-between items-center">
            <span className="satoshi-sm-medium-gray-600 text-nowrap">
              Seagm Credit Balance
            </span>
            <button>
              <DotDotDot />
            </button>
          </div>
          <span className="satoshi-xl-bold-gray-900">
            {seagmBalanceLoading ? (
              <CardSpinner />
            ) : seagmBalanceError ? (
              "Error"
            ) : (
              formatNumber(seagmBalance ?? 0)
            )}
          </span>
        </div>
      </div>

      <div
        className={
          dataupBalance && dataupBalance > 2000
            ? `bg-pry-25 min-w-[234px] rounded-xl`
            : `bg-warn-25 min-w-[234px] rounded-xl`
        }
      >
        <div className="col-6 py-4 px-3">
          <div className="row-2 justify-between items-center">
            <span className="satoshi-sm-medium-gray-600 text-nowrap">
              DataUp Balance
            </span>
            <button>
              <DotDotDot />
            </button>
          </div>
          <span className="satoshi-xl-bold-gray-900">
            {dataupBalanceLoading ? (
              <CardSpinner />
            ) : dataupBalanceError ? (
              "Error"
            ) : (
              `₦${formatCurrency(dataupBalance ?? 0)}`
            )}
          </span>
        </div>
      </div>

      <div className={`bg-sux-25 min-w-[234px] rounded-xl`}>
        <div className="col-6 py-4 px-3">
          <div className="row-2 justify-between items-center">
            <span className="satoshi-sm-medium-gray-600 text-nowrap">
              Transactions Count Today
            </span>
            <button>
              <DotDotDot />
            </button>
          </div>
          <span className="satoshi-xl-bold-gray-900">
            {totalTransactionsLoading ? (
              <CardSpinner />
            ) : totalTransactionsError ? (
              "Error"
            ) : (
              formatNumber(totalTransactions ?? 0)
            )}
          </span>
        </div>
        <Link
          to="/"
          className="flex border-t border-gray-200 py-3 px-6 w-full satoshi-sm-bold-gray-600 text-end"
        >
          View Transactions
        </Link>
      </div>

      <div
        className={
          totalUsers && totalUsers > 2000
            ? `bg-pry-25 min-w-[234px] rounded-xl`
            : `bg-warn-25 min-w-[234px] rounded-xl`
        }
      >
        <div className={`bg-gray-25 min-w-[234px] rounded-xl`}>
          <div className="col-6 py-4 px-3">
            <div className="row-2 justify-between items-center">
              <span className="satoshi-sm-medium-gray-600 text-nowrap">
                Total Users Onboarded
              </span>
              <button>
                <DotDotDot />
              </button>
            </div>
            <span className="satoshi-xl-bold-gray-900">
              {totalUsersLoading ? (
                <CardSpinner />
              ) : totalUsersError ? (
                "Error"
              ) : (
                formatNumber(totalUsers ?? 0)
              )}
            </span>
          </div>
          <Link
            to="/"
            className="flex border-t border-gray-200 py-3 px-6 w-full satoshi-sm-bold-gray-600 text-end"
          >
            View Users
          </Link>
        </div>
      </div>
    </div>
  );
};

const AreaGraph = ({ data }: { data: any }) => (
  <AreaChart width={715} height={200} data={data}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis
      dataKey="name"
      // tick={false} // Hide x-axis ticks
      // axisLine={false} // Hide x-axis line
    >
      <Label
        value="Month" // X-axis title
        position="bottom"
        className="satoshi-xs-medium-gray-600"
        style={{ textAnchor: "middle" }}
      />
    </XAxis>
    <YAxis
      // Optional: Adjust domain for better title visibility
      domain={[0, Math.max(...data.map((item: any) => item.value)) + 5]}
    >
      <Label
        value="Active users" // Y-axis title
        position="left"
        angle={-90}
        className="satoshi-xs-medium-gray-600"
        style={{ textAnchor: "middle" }}
      />
    </YAxis>
    <Tooltip />
    <Area type="monotone" dataKey="value" stroke="#027EC2" fill="#E6F5FE" />
  </AreaChart>
);

export const UserActivity = () => {
  const year = [
    { name: "Jan", value: 60 },
    { name: "Feb", value: 62 },
    { name: "Mar", value: 68 },
    { name: "Apr", value: 66 },
    { name: "May", value: 72 },
    { name: "Jun", value: 67 },
    { name: "Jul", value: 62 },
    { name: "Aug", value: 75 },
    { name: "Sept", value: 77 },
    { name: "Oct", value: 75 },
    { name: "Nov", value: 70 },
    { name: "Dec", value: 79 },
  ];

  const month = [
    { name: "01", value: 60 },
    { name: "02", value: 62 },
    { name: "03", value: 68 },
    { name: "04", value: 66 },
    { name: "05", value: 72 },
    { name: "06", value: 67 },
    { name: "07", value: 62 },
    { name: "08", value: 75 },
    { name: "09", value: 77 },
    { name: "10", value: 75 },
    { name: "11", value: 70 },
    { name: "12", value: 79 },
  ];

  const week = [
    { name: "Mon", value: 60 },
    { name: "Tue", value: 62 },
    { name: "Wed", value: 68 },
    { name: "Thur", value: 66 },
    { name: "Fri", value: 72 },
    { name: "Sat", value: 67 },
    { name: "Sun", value: 62 },
  ];

  const day = [
    { name: "00:00", value: 60 },
    { name: "01:00", value: 62 },
    { name: "02:00", value: 68 },
    { name: "03:00", value: 66 },
    { name: "04:00", value: 72 },
    { name: "05:00", value: 67 },
    { name: "06:00", value: 62 },
    { name: "07:00", value: 75 },
    { name: "08:00", value: 77 },
    { name: "09:00", value: 75 },
    { name: "10:00", value: 70 },
    { name: "11:00", value: 79 },
  ];

  const tabs = [
    { label: "12 months", content: <AreaGraph data={year} /> },
    { label: "30 days", content: <AreaGraph data={month} /> },
    { label: "7 days", content: <AreaGraph data={week} /> },
    { label: "24 hours", content: <AreaGraph data={day} /> },
  ];

  return (
    <div className="rounded-xl border border-gray-200 p-6 col-6">
      <div className="col-5">
        <span className="satoshi-xl-bold-pry-900 dark:text-white">User Activity</span>
        <Tab
          initialActiveIndex={0}
          options={tabs}
          className="w-fit col-5"
          tabListClassName="border-b border-gray-200 row-2"
          tabItemClassName="px-1 pb-2 pt-[1px] satoshi-xs-bold-gray-400"
          activeTabItemClassName="border-b-2 border-pry-700"
        />
      </div>
    </div>
  );
};

export const LeadershipBoard = () => {
  const month = [
    { name: "DarkLordNathan", points: 2000, amount: 200000 },
    { name: "DarkLordNathan", points: 2000, amount: 200000 },
    { name: "DarkLordNathan", points: 2000, amount: 200000 },
    { name: "DarkLordNathan", points: 2000, amount: 200000 },
    { name: "DarkLordNathan", points: 2000, amount: 200000 },
  ];
  const tabs = [
    { label: "This Month", content: <LeadershipComp data={month} /> },
    { label: "All Time", content: <LeadershipComp data={month} /> },
  ];
  return (
    <div className="rounded-xl border border-gray-200 p-6 col-6">
      <div className="col-5">
        <span className="satoshi-xl-bold-pry-900 text-nowrap dark:text-white">
          User leadership board
        </span>
        <Tab
          initialActiveIndex={0}
          options={tabs}
          className="w-fit col-2"
          tabListClassName="border-b border-gray-200 row-2"
          tabItemClassName="px-1 pb-2 pt-[1px] satoshi-xs-bold-gray-400"
          activeTabItemClassName="border-b-2 border-pry-700"
        />
      </div>
    </div>
  );
};

const LeadershipComp = ({ data }: { data: LeadershipProps["data"] }) => {
  return (
    <div className="min-w-[220px]">
      {data.map((item, index) => (
        <div
          key={index}
          className={`row-2 justify-between items-center pt-2 ${
            index < data.length - 1 && "border-b pb-2"
          }`}
        >
          <div className="flex flex-col">
            <span className="satoshi-xs-medium-gray-800">{item.name}</span>
            <span className="inter-xs-normal-pry-600">
              {item.points} points - #{item.amount.toLocaleString()}
            </span>
          </div>
          <EyeIcon />
        </div>
      ))}
    </div>
  );
};

const BarChartComp = ({ data }: { data: any }) => {
  return (
    <div className="w-full h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" />
          <YAxis>
            <Label
              value="Active Users"
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: "middle" }}
            />
          </YAxis>
          <Tooltip />
          <Bar
            dataKey="value"
            fill="#DC6803"
            radius={[8, 8, 0, 0]} // Rounded top corners
            barSize={30} // Optional: control bar width
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const Revenue = () => {
  const year = [
    { name: "Jan", value: 60 },
    { name: "Feb", value: 62 },
    { name: "Mar", value: 68 },
    { name: "Apr", value: 66 },
    { name: "May", value: 72 },
    { name: "Jun", value: 67 },
    { name: "Jul", value: 62 },
    { name: "Aug", value: 75 },
    { name: "Sept", value: 77 },
    { name: "Oct", value: 75 },
    { name: "Nov", value: 70 },
    { name: "Dec", value: 79 },
  ];

  const month = [
    { name: "01", value: 60 },
    { name: "02", value: 62 },
    { name: "03", value: 68 },
    { name: "04", value: 66 },
    { name: "05", value: 72 },
    { name: "06", value: 67 },
    { name: "07", value: 62 },
    { name: "08", value: 75 },
    { name: "09", value: 77 },
    { name: "10", value: 75 },
    { name: "11", value: 70 },
    { name: "12", value: 79 },
  ];

  const week = [
    { name: "Mon", value: 60 },
    { name: "Tue", value: 62 },
    { name: "Wed", value: 68 },
    { name: "Thur", value: 66 },
    { name: "Fri", value: 72 },
    { name: "Sat", value: 67 },
    { name: "Sun", value: 62 },
  ];

  const day = [
    { name: "00:00", value: 60 },
    { name: "01:00", value: 62 },
    { name: "02:00", value: 68 },
    { name: "03:00", value: 66 },
    { name: "04:00", value: 72 },
    { name: "05:00", value: 67 },
    { name: "06:00", value: 62 },
    { name: "07:00", value: 75 },
    { name: "08:00", value: 77 },
    { name: "09:00", value: 75 },
    { name: "10:00", value: 70 },
    { name: "11:00", value: 79 },
  ];

  const tabs = [
    { label: "12 months", content: <BarChartComp data={year} /> },
    { label: "30 days", content: <BarChartComp data={month} /> },
    { label: "7 days", content: <BarChartComp data={week} /> },
    { label: "24 hours", content: <BarChartComp data={day} /> },
  ];

  return (
    <div className="rounded-xl border border-gray-200 p-6 col-6">
      <div className="col-5">
        <div className="row-5 justify-between">
          <span className="satoshi-xl-bold-pry-900 dark:text-white">Revenue</span>
          <button className="rounded-lg border border-gray-300 py-2 px-3 satoshi-sm-bold-sec-900 dark:text-white">
            View report
          </button>
        </div>
        <Tab
          initialActiveIndex={0}
          options={tabs}
          className="w-full col-5"
          tabListClassName="border-b border-gray-200 row-2 w-fit"
          tabItemClassName="px-1 pb-2 pt-[1px] satoshi-xs-bold-gray-400"
          activeTabItemClassName="border-b-2 border-pry-700"
        />
      </div>
    </div>
  );
};
